// Client Radar — buscador cognitivo de leads gobernado por U-CogNet.
// UI NATIVA del dashboard (tokens semánticos → respeta el tema claro/oscuro).
// Consume el proxy gateado /orion/api/client-radar/radar/* (Bearer + X-Org-Id).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Satellite, Loader2, AlertTriangle, Search, Sparkles, ShieldCheck,
  Globe, Building2, Mail, ExternalLink, X, FileText, PenLine, Copy, Check,
  Linkedin, Contact, UserSearch,
} from 'lucide-react';
import {
  getSummary, getLeads, search, preguntar, getLeadDetail, generateDraft,
  enrich, enrichBatch,
  type Summary, type Lead, type LeadDetail, type Draft,
} from '../api';

const num = (n: number) => new Intl.NumberFormat('es-MX').format(n);
const cleanMd = (s: string) =>
  (s || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .trim();

function scoreTone(score: number): string {
  if (score >= 80) return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
  if (score >= 50) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
  return 'bg-muted text-muted-foreground';
}
function tierTone(tier?: string | null): string {
  switch ((tier || '').toUpperCase()) {
    case 'A': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
    case 'B': return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
    case 'C': return 'bg-muted text-muted-foreground';
    default: return '';
  }
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-2xl font-bold tabular-nums text-foreground">{value}</div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

export function ClientRadarPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingLeads, setLoadingLeads] = useState(true);

  const [minScore, setMinScore] = useState(0);
  const [country, setCountry] = useState('');
  const [q, setQ] = useState('');

  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);

  const [askQ, setAskQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);

  // Detalle (drawer)
  const [selected, setSelected] = useState<Lead | null>(null);
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // enriquecimiento de contacto
  const [enriching, setEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState<string | null>(null);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [batchMsg, setBatchMsg] = useState<string | null>(null);

  const vert = summary?.verticals?.[0];

  const refreshLeads = async () => {
    setLoadingLeads(true);
    try {
      const l = await getLeads({ min_score: minScore, country: country || undefined, q: q || undefined, limit: 100 });
      setLeads(l);
    } catch (e: any) {
      setError(e?.message || 'Error cargando leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    getSummary().then(setSummary).catch((e) => setError(e?.message || String(e)));
    refreshLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topSources = useMemo(() => {
    const s = vert?.by_source || {};
    return Object.entries(s).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [vert]);

  const openDetail = async (lead: Lead) => {
    setSelected(lead);
    setDetail(null);
    setDraft(null);
    setDetailErr(null);
    setDetailLoading(true);
    try {
      setDetail(await getLeadDetail(lead.id));
    } catch (e: any) {
      setDetailErr(e?.message || 'No se pudo cargar el detalle');
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setSelected(null); setDetail(null); setDraft(null); };

  const runDraft = async () => {
    if (!selected || draftLoading) return;
    setDraftLoading(true);
    setDraft(null);
    try {
      setDraft(await generateDraft(selected.id));
    } catch (e: any) {
      setDraft({ body: `Error: ${e?.message || 'no se pudo generar'}` });
    } finally {
      setDraftLoading(false);
    }
  };

  const applyContact = (id: string, c: { website?: string | null; email?: string | null; linkedin?: string | null }) => {
    setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, ...c, enriched: true } : x)));
    setSelected((prev) => (prev && prev.id === id ? { ...prev, ...c, enriched: true } : prev));
  };

  const runEnrich = async () => {
    if (!selected || enriching) return;
    setEnriching(true);
    setEnrichMsg(null);
    try {
      const r = await enrich(selected.id);
      applyContact(selected.id, { website: r.website, email: r.email, linkedin: r.linkedin });
      const found = [r.email, r.website, r.linkedin].filter(Boolean).length;
      setEnrichMsg(
        found > 0
          ? `Contacto resuelto (${found} dato${found > 1 ? 's' : ''}).`
          : 'El modelo no encontró contacto público de este lead (común en papers/artículos; funciona mejor con empresas/orgs).'
      );
    } catch (e: any) {
      setEnrichMsg(`Error: ${e?.message || 'no se pudo resolver'}`);
    } finally {
      setEnriching(false);
    }
  };

  const runBatchEnrich = async () => {
    if (batchEnriching) return;
    setBatchEnriching(true);
    setBatchMsg(null);
    try {
      const r = await enrichBatch(25, { min_score: minScore || undefined });
      setBatchMsg(
        `Lote: ${r.scanned} escaneados · ${r.enriched} enriquecidos · ${r.with_email} con email · ${r.with_website} con sitio · ${r.with_linkedin} con LinkedIn.`
      );
      await refreshLeads();
    } catch (e: any) {
      setBatchMsg(`Error: ${e?.message || 'no se pudo enriquecer el lote'}`);
    } finally {
      setBatchEnriching(false);
    }
  };

  const copyDraft = () => {
    if (!draft) return;
    const text = `${draft.subject ? draft.subject + '\n\n' : ''}${draft.body || ''}`;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  const runSearch = async () => {
    if (!searchQ.trim() || searching) return;
    setSearching(true); setSearchMsg(null);
    try {
      const r = await search(searchQ.trim());
      const added = (r.added ?? r.count ?? r.n ?? '') as any;
      setSearchMsg(`Búsqueda completada${added !== '' ? ` · ${added} leads nuevos` : ''}. Actualizando…`);
      await Promise.all([getSummary().then(setSummary), refreshLeads()]);
    } catch (e: any) {
      setSearchMsg(`Error: ${e?.message || 'no se pudo completar la búsqueda'}`);
    } finally {
      setSearching(false);
    }
  };

  const runAsk = async () => {
    if (!askQ.trim() || asking) return;
    setAsking(true); setAnswer(null);
    try {
      const r = await preguntar(askQ.trim());
      const text = (r.answer ?? r.respuesta ?? r.text ?? r.mensaje ?? JSON.stringify(r)) as string;
      setAnswer(cleanMd(String(text)));
    } catch (e: any) {
      setAnswer(`Error: ${e?.message || 'no se pudo responder'}`);
    } finally {
      setAsking(false);
    }
  };

  if (error && !summary) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <AlertTriangle className="text-destructive" size={36} />
        <p className="text-foreground font-medium">Client Radar no disponible</p>
        <p className="text-muted-foreground text-sm max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Satellite className="text-primary" size={22} />
        <h1 className="text-xl font-bold text-foreground">Client Radar</h1>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          <ShieldCheck size={12} /> Gobernado por U-CogNet
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Leads totales" value={num(vert?.leads ?? 0)} />
        <Stat label="Cualificados" value={num(vert?.qualified ?? 0)} />
        <Stat label="Verticales" value={summary?.verticals?.length ?? 0} />
        <Stat label="Fuentes" value={topSources.length} hint={topSources.map(([s]) => s).join(' · ')} />
      </div>

      {/* Deep-search */}
      <div className="rounded-xl border border-border bg-card p-4">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles size={15} className="text-primary" /> Pídele a U-CogNet que busque leads
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            placeholder="ej: reclutadores de embodied AI en Europa"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button
            onClick={runSearch}
            disabled={searching || !searchQ.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {searching ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
            {searching ? 'Buscando…' : 'Buscar leads'}
          </button>
        </div>
        {searching && (
          <p className="mt-2 text-xs text-muted-foreground">
            El deep-search consulta 6 fuentes y gobierna cada lead — puede tardar ~2-3 min. No cierres esta pestaña.
          </p>
        )}
        {searchMsg && <p className="mt-2 text-xs text-foreground">{searchMsg}</p>}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refreshLeads()}
          placeholder="filtrar (nombre / organización / keyword)"
          className="min-w-[220px] flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && refreshLeads()}
          placeholder="país"
          className="w-32 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <select
          value={minScore}
          onChange={(e) => setMinScore(Number(e.target.value))}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value={0}>score ≥ 0</option>
          <option value={30}>score ≥ 30</option>
          <option value={50}>score ≥ 50</option>
          <option value={80}>score ≥ 80</option>
        </select>
        <button
          onClick={refreshLeads}
          className="rounded-lg border border-border bg-muted px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/70"
        >
          Filtrar
        </button>
        <button
          onClick={runBatchEnrich}
          disabled={batchEnriching}
          title="Resuelve contacto (web/email/LinkedIn) de los leads visibles"
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/70 disabled:opacity-50"
        >
          {batchEnriching ? <Loader2 className="animate-spin" size={14} /> : <UserSearch size={14} />}
          {batchEnriching ? 'Enriqueciendo…' : 'Enriquecer lote'}
        </button>
      </div>
      {batchMsg && <p className="-mt-2 text-xs text-muted-foreground">{batchMsg}</p>}

      {/* Tabla de leads */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Tier · Score</th>
              <th className="px-4 py-3 font-medium">Nombre / Organización</th>
              <th className="px-4 py-3 font-medium">País</th>
              <th className="px-4 py-3 font-medium">Tipo · Fuente</th>
              <th className="px-4 py-3 font-medium">Contacto</th>
              <th className="px-4 py-3 font-medium">Gob.</th>
            </tr>
          </thead>
          <tbody>
            {loadingLeads ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground"><Loader2 className="mx-auto animate-spin" size={22} /></td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Sin leads con estos filtros. Baja el score o corre un deep-search arriba.</td></tr>
            ) : (
              leads.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => openDetail(l)}
                  className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {l.tier && (
                        <span className={`rounded-md px-1.5 py-0.5 text-xs font-bold ${tierTone(l.tier)}`}>{l.tier}</span>
                      )}
                      <span className={`rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums ${scoreTone(l.score)}`}>{Math.round(l.score)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{l.name}</div>
                    {l.org && l.org !== l.name && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground"><Building2 size={11} /> {l.org}</div>
                    )}
                    {(l.reason || l.role) && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{l.reason || l.role}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {l.country && <span className="inline-flex items-center gap-1"><Globe size={12} /> {l.country}</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div>{l.org_type || '—'}</div>
                    {l.source && <div className="text-xs opacity-70">{l.source}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {l.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-primary hover:underline"><Mail size={12} /> {l.email}</a>}
                      {l.website && <a onClick={(e) => e.stopPropagation()} href={l.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink size={12} /> sitio</a>}
                      {l.linkedin && <a onClick={(e) => e.stopPropagation()} href={l.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><Linkedin size={12} /> LinkedIn</a>}
                      {!l.email && !l.website && !l.linkedin && <span className="text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {l.governed ? <ShieldCheck size={15} className="text-emerald-600 dark:text-emerald-400" /> : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {leads.length > 0 && (
        <p className="text-xs text-muted-foreground">Mostrando {leads.length} leads · haz clic en uno para ver su brief.</p>
      )}

      {/* Analista */}
      <div className="rounded-xl border border-border bg-card p-4">
        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles size={15} className="text-primary" /> Pregúntale al analista
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={askQ}
            onChange={(e) => setAskQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runAsk()}
            placeholder="ej: mejores prospectos y cómo abordarlos"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button
            onClick={runAsk}
            disabled={asking || !askQ.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {asking ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
            {asking ? 'Analizando…' : 'Preguntar'}
          </button>
        </div>
        {answer && <div className="mt-3 whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-sm text-foreground">{answer}</div>}
      </div>

      {/* ── Drawer de detalle ── */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={closeDetail} />
          <div className="relative z-10 flex h-full w-full max-w-lg flex-col overflow-y-auto border-l border-border bg-background shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-border p-4">
              <div>
                <div className="flex items-center gap-2">
                  {selected.tier && <span className={`rounded-md px-1.5 py-0.5 text-xs font-bold ${tierTone(selected.tier)}`}>{selected.tier}</span>}
                  <span className={`rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums ${scoreTone(selected.score)}`}>{Math.round(selected.score)}</span>
                  {selected.source && <span className="text-xs text-muted-foreground">{selected.source}</span>}
                </div>
                <h2 className="mt-1 text-base font-bold text-foreground">{selected.name}</h2>
                <p className="text-xs text-muted-foreground">
                  {[selected.org_type, selected.country, selected.role].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button onClick={closeDetail} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted" aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 space-y-4 p-4">
              {detailLoading ? (
                <div className="space-y-3 py-6">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <Loader2 className="animate-spin text-primary" size={18} /> U-CogNet está investigando este lead…
                  </div>
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-center gap-2"><Search size={12} className="text-primary" /> Buscando sus trabajos recientes en OpenAlex</li>
                    <li className="flex items-center gap-2"><Sparkles size={12} className="text-primary" /> Analizando señales con el LLM gobernado</li>
                    <li className="flex items-center gap-2"><ShieldCheck size={12} className="text-primary" /> Redactando brief y firmando decisión</li>
                  </ul>
                </div>
              ) : detailErr ? (
                <p className="text-sm text-destructive">{detailErr}</p>
              ) : detail ? (
                <>
                  {(() => {
                    const b = detail.brief || {};
                    const has = Object.values(b).some((v) => v && String(v).trim());
                    if (!has) {
                      return (
                        <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                          El modelo no encontró suficiente información pública de este lead para un brief detallado.
                          Abajo puedes ver qué consultó y su decisión gobernada.
                        </p>
                      );
                    }
                    return ([
                      ['Perfil', b.profile],
                      ['Por qué es prospecto', b.why_prospect],
                      ['Cómo abordarlo', b.how_to_approach],
                      ['Señales', b.signals],
                    ] as const).map(([label, val]) =>
                      val ? (
                        <div key={label}>
                          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h3>
                          <p className="whitespace-pre-wrap text-sm text-foreground">{cleanMd(String(val))}</p>
                        </div>
                      ) : null
                    );
                  })()}

                  {/* Qué investigó el modelo (trazabilidad del proceso) */}
                  {detail.research && detail.research.length > 0 && (
                    <div>
                      <h3 className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <FileText size={12} /> Qué investigó ({detail.research.length})
                      </h3>
                      <ul className="space-y-1 rounded-lg bg-muted/40 p-3 text-xs text-foreground">
                        {detail.research.map((r, i) => (
                          <li key={i} className="leading-snug">{cleanMd(String(r)).replace(/^•\s*/, '')}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Trazabilidad / gobernanza — siempre visible */}
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-3 text-[11px] text-muted-foreground">
                    <div className="mb-1 flex items-center gap-1.5 font-semibold uppercase tracking-wide">
                      <ShieldCheck size={12} className="text-emerald-600 dark:text-emerald-400" /> Trazabilidad
                    </div>
                    <div className="grid grid-cols-1 gap-0.5">
                      <span>Fuente del lead: <b className="text-foreground">{selected.source || '—'}</b></span>
                      <span>Gobernado: <b className="text-foreground">{selected.governed ? 'sí' : 'no'}</b>{selected.tier ? ` · tier ${selected.tier}` : ''}</span>
                      <span>Brief: <b className="text-foreground">{detail.cached ? 'de caché' : 'investigado en vivo'}</b></span>
                      {detail.decision_id && <span className="break-all">decision_id: {detail.decision_id}</span>}
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Sin datos para este lead.</p>
              )}

              {/* Acercamiento */}
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground"><PenLine size={14} className="text-primary" /> Acercamiento</span>
                  <button
                    onClick={runDraft}
                    disabled={draftLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {draftLoading ? <Loader2 className="animate-spin" size={13} /> : <FileText size={13} />}
                    {draftLoading ? 'Redactando…' : draft ? 'Regenerar' : 'Generar acercamiento'}
                  </button>
                </div>
                {draftLoading && (
                  <p className="mt-2 text-xs text-muted-foreground">U-CogNet redacta un outreach personalizado y gobernado (sin spam ni claims inflados)…</p>
                )}
                {draft && (
                  draft.withheld ? (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      Retenido por gobernanza{draft.reason ? `: ${draft.reason}` : '.'}
                    </p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {draft.subject && (
                        <div>
                          <div className="text-xs uppercase tracking-wide text-muted-foreground">Asunto</div>
                          <div className="text-sm font-medium text-foreground">{draft.subject}</div>
                        </div>
                      )}
                      {draft.body && (
                        <div className="whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-sm text-foreground">{draft.body}</div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <button onClick={copyDraft} className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copiado' : 'Copiar'}
                        </button>
                        {draft.decision_id && (
                          <span className="break-all text-[10px] text-muted-foreground">decision_id: {draft.decision_id}</span>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Contacto (resolución gobernada) */}
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground"><Contact size={14} className="text-primary" /> Contacto</span>
                  <button
                    onClick={runEnrich}
                    disabled={enriching}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/70 disabled:opacity-50"
                  >
                    {enriching ? <Loader2 className="animate-spin" size={13} /> : <UserSearch size={13} />}
                    {enriching ? 'Resolviendo…' : (selected.email || selected.website || selected.linkedin) ? 'Re-resolver' : 'Resolver contacto'}
                  </button>
                </div>
                {enriching && (
                  <p className="mt-2 text-xs text-muted-foreground">U-CogNet busca su web (DuckDuckGo + fetch) y extrae contacto, gobernado…</p>
                )}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {selected.email && <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-primary hover:underline"><Mail size={13} /> {selected.email}</a>}
                  {selected.website && <a href={selected.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink size={13} /> sitio</a>}
                  {selected.linkedin && <a href={selected.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><Linkedin size={13} /> LinkedIn</a>}
                  {!selected.email && !selected.website && !selected.linkedin && !enrichMsg && (
                    <span className="text-muted-foreground">Sin contacto aún — dale "Resolver contacto".</span>
                  )}
                </div>
                {enrichMsg && <p className="mt-2 text-xs text-muted-foreground">{enrichMsg}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
